import { jsPDF } from 'jspdf'
import { KVAnnotation, KVMeasurement, KVPrimitive } from 'types'

import {
  addManropeFont,
  formatPositionText,
  generatePDFFooter,
  generatePDFHeader,
  splitTextToFitWidth,
} from './pdfExportUtils'

/**
 * Convert annotations, primitives, and measurements to PDF with screenshot images.
 *
 * @param annotations - Array of KV annotations to export
 * @param primitives - Array of KV primitives to export
 * @param measurements - Array of KV measurements to export
 * @param screenshotData - Map of item ID to screenshot data
 * @param metadata - Optional metadata (component name, site name, organization)
 * @returns Promise resolving to PDF blob
 */
export async function convertAnnotationsToPDFWithScreenshots(
  annotations: KVAnnotation[] | undefined,
  primitives: KVPrimitive[] | undefined,
  measurements: KVMeasurement[] | undefined,
  screenshotData: Record<string, { imageBase64?: string; path: string }>,
  metadata?: {
    componentName?: string
    siteName?: string
    organizationName?: string
  }
): Promise<Blob> {
  const decimalPlaces = 3

  const totalCount =
    (annotations?.length ?? 0) +
    (primitives?.length ?? 0) +
    (measurements?.length ?? 0)

  if (totalCount === 0) {
    return await generateEmptyAnnotationsPDF(metadata)
  }

  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  await addManropeFont(pdf)
  await generatePDFHeader(pdf, metadata)
  generateItemsSummary(
    pdf,
    annotations ?? [],
    primitives ?? [],
    measurements ?? []
  )

  // Start content on a new page
  pdf.addPage()
  await generateBlackBarAndLogo(pdf)

  await generateAllItemsContentWithScreenshots(
    pdf,
    annotations ?? [],
    primitives ?? [],
    measurements ?? [],
    screenshotData,
    decimalPlaces
  )
  generatePDFFooter(pdf)

  return pdf.output('blob')
}

async function generateBlackBarAndLogo(pdf: jsPDF): Promise<void> {
  const pageWidth = pdf.internal.pageSize.getWidth()
  const blackBarHeight = 20

  pdf.setFillColor(0, 0, 0)
  pdf.rect(0, 0, pageWidth, blackBarHeight, 'F')

  try {
    const logoDataUrl = await convertSvgToPngDataUrl(144)
    const logoSize = 12
    pdf.addImage(logoDataUrl, 'PNG', 10, 4, logoSize, logoSize)
  } catch (error) {
    console.warn('Failed to add logo to PDF:', error)
  }

  pdf.setTextColor(255, 255, 255)
  pdf.setFontSize(12)
  pdf.setFont('Manrope', 'bold')
  pdf.text('GECKO ROBOTICS', 25, 12)

  const annotationReportText = 'Annotation Report'
  const annotationReportWidth = pdf.getTextWidth(annotationReportText)
  pdf.text(annotationReportText, pageWidth - annotationReportWidth - 10, 12)

  pdf.setTextColor(0, 0, 0)
}

function generateItemsSummary(
  pdf: jsPDF,
  annotations: KVAnnotation[],
  primitives: KVPrimitive[],
  measurements: KVMeasurement[]
): void {
  const pageWidth = pdf.internal.pageSize.getWidth()
  let yPosition = 90

  pdf.setFontSize(12)
  pdf.setFont('Manrope', 'bold')
  pdf.text('Summary', 20, yPosition)
  yPosition += 8

  pdf.setFontSize(10)
  pdf.setFont('Manrope', 'normal')

  const annotationCount = annotations.length
  const primitiveCount = primitives.length
  const measurementCount = measurements.length
  const totalCount = annotationCount + primitiveCount + measurementCount

  pdf.text(`Total Items: ${totalCount}`, 20, yPosition)
  yPosition += 5
  pdf.text(`  Annotations: ${annotationCount}`, 25, yPosition)
  yPosition += 5
  pdf.text(`  Primitives: ${primitiveCount}`, 25, yPosition)
  yPosition += 5
  pdf.text(`  Measurements: ${measurementCount}`, 25, yPosition)
  yPosition += 10

  pdf.setLineWidth(0.5)
  pdf.line(20, yPosition, pageWidth - 20, yPosition)
}

async function generateAllItemsContentWithScreenshots(
  pdf: jsPDF,
  annotations: KVAnnotation[],
  primitives: KVPrimitive[],
  measurements: KVMeasurement[],
  screenshotData: Record<string, { imageBase64?: string; path: string }>,
  decimalPlaces: number
): Promise<void> {
  // Generate annotations first
  if (annotations.length > 0) {
    await generateAnnotationsContentWithScreenshots(
      pdf,
      annotations,
      screenshotData,
      decimalPlaces,
      35
    )
  }

  // Generate primitives on a new page
  if (primitives.length > 0) {
    pdf.addPage()
    await generateBlackBarAndLogo(pdf)
    await generatePrimitivesContentWithScreenshots(
      pdf,
      primitives,
      screenshotData,
      decimalPlaces,
      35
    )
  }

  // Generate measurements on a new page
  if (measurements.length > 0) {
    pdf.addPage()
    await generateBlackBarAndLogo(pdf)
    await generateMeasurementsContentWithScreenshots(
      pdf,
      measurements,
      screenshotData,
      decimalPlaces,
      35
    )
  }
}

async function generateAnnotationsContentWithScreenshots(
  pdf: jsPDF,
  annotations: KVAnnotation[],
  screenshotData: Record<string, { imageBase64?: string; path: string }>,
  decimalPlaces: number,
  startYPosition: number = 35
): Promise<number> {
  let yPosition = startYPosition
  const pageHeight = pdf.internal.pageSize.getHeight()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const lineHeight = 6
  const sectionSpacing = 12
  const imageWidth = pageWidth - 40 // Leave margins
  const imageMaxHeight = 100 // Maximum height for images

  for (let index = 0; index < annotations.length; index++) {
    const annotation = annotations[index]
    const screenshot = screenshotData[annotation.id]

    // Calculate space needed for this annotation
    const hasScreenshot = screenshot?.imageBase64
    const estimatedTextHeight = 80
    const estimatedImageHeight = hasScreenshot ? imageMaxHeight + 10 : 0
    const spaceNeeded =
      estimatedTextHeight + estimatedImageHeight + sectionSpacing

    // Check if we need a new page
    if (yPosition + spaceNeeded > pageHeight - 20) {
      pdf.addPage()
      await generateBlackBarAndLogo(pdf)
      yPosition = 35
    }

    pdf.setFontSize(14)
    pdf.setFont('Manrope', 'bold')
    const annotationTitle = `Annotation ${index + 1}`
    pdf.text(annotationTitle, 20, yPosition)
    yPosition += lineHeight + 2

    pdf.setFontSize(10)
    pdf.setFont('Manrope', 'normal')

    if (annotation.label) {
      const labelText = `Label: ${annotation.label}`
      const labelLines = splitTextToFitWidth(pdf, labelText, pageWidth - 50)
      labelLines.forEach((line) => {
        pdf.text(line, 25, yPosition)
        yPosition += lineHeight
      })
    }

    if (annotation.notes) {
      pdf.text('Notes:', 25, yPosition)
      yPosition += lineHeight
      const notesLines = splitTextToFitWidth(
        pdf,
        annotation.notes,
        pageWidth - 50
      )
      notesLines.forEach((line) => {
        pdf.text(line, 30, yPosition)
        yPosition += lineHeight
      })
    }

    if (annotation.geometry?.kind === 'point' && annotation.geometry.position) {
      const positionText = formatPositionText(
        annotation.geometry.position.toString(),
        decimalPlaces
      )
      pdf.text(`Position: ${positionText}`, 25, yPosition)
      yPosition += lineHeight
    }

    if (annotation.severity) {
      pdf.text(`Severity: ${annotation.severity}`, 25, yPosition)
      yPosition += lineHeight
    }

    if (annotation.type) {
      pdf.text(`Type: ${annotation.type}`, 25, yPosition)
      yPosition += lineHeight
    }

    if (hasScreenshot) {
      yPosition += 5

      try {
        const imgData = screenshot.imageBase64!

        // Get image dimensions to calculate aspect ratio
        const img = new Image()
        await new Promise((resolve, reject) => {
          img.onload = () => resolve(null)
          img.onerror = reject
          img.src = imgData
        })

        const aspectRatio = img.width / img.height
        let imgWidth = imageWidth
        let imgHeight = imageWidth / aspectRatio

        if (imgHeight > imageMaxHeight) {
          imgHeight = imageMaxHeight
          imgWidth = imageMaxHeight * aspectRatio
        }

        const imgX = (pageWidth - imgWidth) / 2

        // Check if we need a new page for the image
        if (yPosition + imgHeight > pageHeight - 20) {
          pdf.addPage()
          await generateBlackBarAndLogo(pdf)
          yPosition = 35
        }

        pdf.addImage(imgData, 'PNG', imgX, yPosition, imgWidth, imgHeight)
        yPosition += imgHeight + 5
      } catch {
        // Skip image if loading fails
      }
    }

    yPosition += sectionSpacing
  }

  return yPosition
}

async function generatePrimitivesContentWithScreenshots(
  pdf: jsPDF,
  primitives: KVPrimitive[],
  screenshotData: Record<string, { imageBase64?: string; path: string }>,
  decimalPlaces: number,
  startYPosition: number = 35
): Promise<number> {
  let yPosition = startYPosition
  const pageHeight = pdf.internal.pageSize.getHeight()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const lineHeight = 6
  const sectionSpacing = 12
  const imageWidth = pageWidth - 40
  const imageMaxHeight = 100

  for (let index = 0; index < primitives.length; index++) {
    const primitive = primitives[index]
    const screenshot = screenshotData[primitive.id]

    const hasScreenshot = screenshot?.imageBase64
    const estimatedTextHeight = 60
    const estimatedImageHeight = hasScreenshot ? imageMaxHeight + 10 : 0
    const spaceNeeded =
      estimatedTextHeight + estimatedImageHeight + sectionSpacing

    if (yPosition + spaceNeeded > pageHeight - 20) {
      pdf.addPage()
      await generateBlackBarAndLogo(pdf)
      yPosition = 35
    }

    pdf.setFontSize(14)
    pdf.setFont('Manrope', 'bold')
    const primitiveTitle = `Primitive ${index + 1}`
    pdf.text(primitiveTitle, 20, yPosition)
    yPosition += lineHeight + 2

    pdf.setFontSize(10)
    pdf.setFont('Manrope', 'normal')

    if (primitive.label) {
      const labelText = `Label: ${primitive.label}`
      const labelLines = splitTextToFitWidth(pdf, labelText, pageWidth - 50)
      labelLines.forEach((line) => {
        pdf.text(line, 25, yPosition)
        yPosition += lineHeight
      })
    }

    if (primitive.notes) {
      pdf.text('Notes:', 25, yPosition)
      yPosition += lineHeight
      const notesLines = splitTextToFitWidth(
        pdf,
        primitive.notes,
        pageWidth - 50
      )
      notesLines.forEach((line) => {
        pdf.text(line, 30, yPosition)
        yPosition += lineHeight
      })
    }

    if (primitive.geometry) {
      pdf.text(`Type: ${primitive.geometry}`, 25, yPosition)
      yPosition += lineHeight
    }

    if (primitive.volume !== undefined) {
      pdf.text(
        `Volume: ${primitive.volume.toFixed(decimalPlaces)}`,
        25,
        yPosition
      )
      yPosition += lineHeight
    }

    if (primitive.area !== undefined) {
      pdf.text(`Area: ${primitive.area.toFixed(decimalPlaces)}`, 25, yPosition)
      yPosition += lineHeight
    }

    if (hasScreenshot) {
      yPosition += 5

      try {
        const imgData = screenshot.imageBase64!
        const img = new Image()
        await new Promise((resolve, reject) => {
          img.onload = () => resolve(null)
          img.onerror = reject
          img.src = imgData
        })

        const aspectRatio = img.width / img.height
        let imgWidth = imageWidth
        let imgHeight = imageWidth / aspectRatio

        if (imgHeight > imageMaxHeight) {
          imgHeight = imageMaxHeight
          imgWidth = imageMaxHeight * aspectRatio
        }

        const imgX = (pageWidth - imgWidth) / 2

        if (yPosition + imgHeight > pageHeight - 20) {
          pdf.addPage()
          await generateBlackBarAndLogo(pdf)
          yPosition = 35
        }

        pdf.addImage(imgData, 'PNG', imgX, yPosition, imgWidth, imgHeight)
        yPosition += imgHeight + 5
      } catch {
        // Skip image if loading fails
      }
    }

    yPosition += sectionSpacing
  }

  return yPosition
}

async function generateMeasurementsContentWithScreenshots(
  pdf: jsPDF,
  measurements: KVMeasurement[],
  screenshotData: Record<string, { imageBase64?: string; path: string }>,
  decimalPlaces: number,
  startYPosition: number = 35
): Promise<number> {
  let yPosition = startYPosition
  const pageHeight = pdf.internal.pageSize.getHeight()
  const pageWidth = pdf.internal.pageSize.getWidth()
  const lineHeight = 6
  const sectionSpacing = 12
  const imageWidth = pageWidth - 40
  const imageMaxHeight = 100

  for (let index = 0; index < measurements.length; index++) {
    const measurement = measurements[index]
    const screenshot = screenshotData[measurement.id]

    const hasScreenshot = screenshot?.imageBase64
    const estimatedTextHeight = 60
    const estimatedImageHeight = hasScreenshot ? imageMaxHeight + 10 : 0
    const spaceNeeded =
      estimatedTextHeight + estimatedImageHeight + sectionSpacing

    if (yPosition + spaceNeeded > pageHeight - 20) {
      pdf.addPage()
      await generateBlackBarAndLogo(pdf)
      yPosition = 35
    }

    pdf.setFontSize(14)
    pdf.setFont('Manrope', 'bold')
    const measurementTitle = `Measurement ${index + 1}`
    pdf.text(measurementTitle, 20, yPosition)
    yPosition += lineHeight + 2

    pdf.setFontSize(10)
    pdf.setFont('Manrope', 'normal')

    if (measurement.label) {
      const labelText = `Label: ${measurement.label}`
      const labelLines = splitTextToFitWidth(pdf, labelText, pageWidth - 50)
      labelLines.forEach((line) => {
        pdf.text(line, 25, yPosition)
        yPosition += lineHeight
      })
    }

    // Display number of segments
    if (measurement.segments && measurement.segments.length > 0) {
      pdf.text(`Segments: ${measurement.segments.length}`, 25, yPosition)
      yPosition += lineHeight
    }

    // Display finalized status
    if (measurement.isMeasurementFinalized) {
      pdf.text('Status: Finalized', 25, yPosition)
      yPosition += lineHeight
    }

    if (hasScreenshot) {
      yPosition += 5

      try {
        const imgData = screenshot.imageBase64!
        const img = new Image()
        await new Promise((resolve, reject) => {
          img.onload = () => resolve(null)
          img.onerror = reject
          img.src = imgData
        })

        const aspectRatio = img.width / img.height
        let imgWidth = imageWidth
        let imgHeight = imageWidth / aspectRatio

        if (imgHeight > imageMaxHeight) {
          imgHeight = imageMaxHeight
          imgWidth = imageMaxHeight * aspectRatio
        }

        const imgX = (pageWidth - imgWidth) / 2

        if (yPosition + imgHeight > pageHeight - 20) {
          pdf.addPage()
          await generateBlackBarAndLogo(pdf)
          yPosition = 35
        }

        pdf.addImage(imgData, 'PNG', imgX, yPosition, imgWidth, imgHeight)
        yPosition += imgHeight + 5
      } catch {
        // Skip image if loading fails
      }
    }

    yPosition += sectionSpacing
  }

  return yPosition
}

async function generateEmptyAnnotationsPDF(metadata?: {
  componentName?: string
  siteName?: string
  organizationName?: string
}): Promise<Blob> {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: 'a4',
  })

  await addManropeFont(pdf)
  await generatePDFHeader(pdf, metadata)

  pdf.setFontSize(12)
  pdf.setFont('Manrope', 'normal')
  pdf.text('No annotations available for export.', 20, 90)

  generatePDFFooter(pdf)

  return pdf.output('blob')
}

function convertSvgToPngDataUrl(size: number = 48): Promise<string> {
  return new Promise((resolve, reject) => {
    // SVG with white fill for the logo
    const svgContent = `
      <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 295.11 295.11" width="${size}" height="${size}">
        <g>
          <path fill="white" d="m139.88,105.79c-12.03,0-20.66,8.4-20.66,19.75s8.85,19.75,20.66,19.75,20.88-8.4,20.88-19.75-9.08-19.75-20.88-19.75Z"/>
          <path fill="white" d="m0,0v295.11h295.11V0H0Zm210.62,89.44h-13.99c-5.45,0-9.76,2.72-10.9,7.04,5.68,8.17,9.08,17.93,9.08,29.06,0,29.28-23.61,51.08-54.94,51.08-5.9,0-11.58-.68-17.03-2.27-3.4,2.27-5.22,5.45-5.22,8.85,0,5.22,4.31,8.63,10.44,8.63h36.09c26,0,46.45,19.15,46.45,44.49h-36.24c-.91-7.49-7.26-12.49-16.57-12.49h-36.09c-22.47,0-37.23-12.94-37.23-32.23,0-12.26,6.13-24.06,15.89-29.96-9.53-9.08-15.21-21.79-15.21-36.09,0-29.28,23.38-51.08,54.71-51.08,8.85,0,17.03,1.82,24.29,4.77,3.41-12.49,14.98-20.43,30.87-20.43h15.58v30.65Z"/>
        </g>
      </svg>
    `

    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    canvas.width = size
    canvas.height = size

    const img = new Image()
    const svgBlob = new Blob([svgContent], {
      type: 'image/svg+xml;charset=utf-8',
    })
    const url = URL.createObjectURL(svgBlob)

    img.onload = () => {
      if (ctx) {
        ctx.drawImage(img, 0, 0, size, size)
        const dataUrl = canvas.toDataURL('image/png', 1.0)
        URL.revokeObjectURL(url)
        resolve(dataUrl)
      } else {
        reject(new Error('Failed to get canvas context'))
      }
    }

    img.onerror = () => {
      URL.revokeObjectURL(url)
      reject(new Error('Failed to load SVG image'))
    }

    img.src = url
  })
}
